import { S } from './state.js';
import { cardLayout } from './layout.js';
import { Audio } from './audio.js';
import { BAL } from './config.js';
import { ARCH_INFO, ITEMS, TIERS } from './data/items.js';
import { ITEM_TAGS, WEAPONS, WEAPON_IDX, buildPrereqNote, levelStatDead, offerWeightMul, weaponInteractionNote, weaponStatNote } from './data/weapons.js';
import { rnd, rrange } from './fx.js';
import { bloodCost, grantItem, randomItemOfTier, randomMythic, rollTier, shrineItemPrice, vaultPrice } from './economy.js';
import { player } from './entities.js';
import { pushOutOfRocks } from './world.js';
import { ctx } from './canvas.js';
import { aim, pointIn } from './input.js';
import { dropCoins, popText, spawnParticle } from './procs.js';
import { hexA } from './render.js';
import { roundRect } from './screens.js';
import { autoRing, grantGear, makeGear } from './shop.js';
import { perkDiffLines, recomputeStats, statDiffLines } from './stats.js';
import { fitText, fitTip, wrapText } from './ui.js';
// ---- Power pickups (extra small ways to get stronger) ----
export function dropPickup(x, y, kind) {
  const p = { x: x + rrange(-12, 12), y: y + rrange(-12, 12), kind, got: false, mag: false, spd: 0, ph: rrange(0, 6.28) };
  pushOutOfRocks(p, 16);   // drops stay walkable-to
  if (kind === 'weapon') {
    p.wkey = WEAPONS[1 + Math.floor(rnd() * (WEAPONS.length - 1))].key; p.life = 900;   // gun drops despawn after ~15s
    const guns = S.pickups.filter(q => !q.got && q.kind === 'weapon');
    if (guns.length >= 10) { const i = S.pickups.indexOf(guns[0]); if (i >= 0) S.pickups.splice(i, 1); }   // keep at most 10 guns on the ground — shed the oldest so a boss/elite-heavy wave can't litter the arena
  }
  else p.life = 600;   // hearts/shards despawn after ~10s — a god-build drops thousands; un-grabbed ones used to carpet the arena & tank the frame rate (5 per-frame loops + emoji draws over an unbounded pile)
  S.pickups.push(p);
  if (S.pickups.length > 400) {   // hard safety cap — shed the oldest un-grabbed heart/shard so the list can't run away
    const i = S.pickups.findIndex(q => !q.got && q.kind !== 'weapon');
    if (i >= 0) S.pickups.splice(i, 1);
  }
}
export function collectPickup(p) {
  if (p.got) return;
  p.got = true;
  if (p.kind === 'heart') {                       // a small heal — softens the rough early game
    player.hp = Math.min(S.MAX_HP, player.hp + S.MAX_HP * BAL.econ.heartHeal);
    popText({ x: p.x, y: p.y - 10 }, '+HP');
    for (let k = 0; k < 6; k++) spawnParticle(p.x, p.y, 'bounce');
    Audio.play('shrine');
  } else {                                        // power shard — permanent run damage, just a bit
    S.runBonusDmg += BAL.econ.shardDmg; recomputeStats();
    popText({ x: p.x, y: p.y - 10 }, '+DMG');
    for (let k = 0; k < 8; k++) spawnParticle(p.x, p.y, 'gold');
    Audio.play('powerup');
  }
}
export function buyBloodShrine(s) {   // pay the HP toll for a high-rarity item
  const cost = bloodCost();
  if (player.hp <= cost + 1) return false;          // can't safely afford the sacrifice
  S.hpPenalty += cost; S.bloodBoughtRun = true;         // permanent max-HP cut (+ flags the Blood Pact achievement)
  grantItem(s.item.key);
  player.hp = Math.max(1, Math.min(S.MAX_HP, player.hp - cost));
  s.taken = true;
  Audio.play('hurt'); Audio.play('powerup');
  popText({ x: s.x, y: s.y }, s.item.desc);   // show WHAT it does on pickup, not just the name
  S.shake = Math.max(S.shake, 10); S.flash = Math.max(S.flash, S.save.settings.reduceFlash ? 0.1 : 0.25);
  for (let k = 0; k < 26; k++) spawnParticle(s.x, s.y, 'normal');
  return true;
}
export function confirmBlood() {   // SACRIFICE chosen on the Blood Shrine prompt
  if (S.bloodConfirm) { buyBloodShrine(S.bloodConfirm); S.bloodConfirm = null; }
}
// Blood Shrine confirmation: show the item + HP toll and require a yes/no before paying
export function bloodConfirmOverlay() {
  const s = S.bloodConfirm; if (!s) return;
  S.uiButtons = [];
  const cost = bloodCost();
  const affordable = player.hp > cost + 1;
  const it = s.item, col = TIERS[it.tier].color;

  ctx.fillStyle = 'rgba(6,10,24,.86)'; ctx.fillRect(0, 0, S.W, S.H);
  const pw = Math.min(460, S.W - 32), ph = 326, px = (S.W - pw) / 2, py = (S.H - ph) / 2;
  ctx.fillStyle = 'rgba(22,11,16,.97)'; roundRect(px, py, pw, ph, 18); ctx.fill();
  ctx.strokeStyle = 'rgba(255,93,107,.5)'; ctx.lineWidth = 1.5; ctx.stroke();

  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#ff8d8d'; ctx.font = 'bold 15px system-ui';
  ctx.fillText('🩸 BLOOD SHRINE', S.W / 2, py + 36);

  ctx.font = '46px system-ui'; ctx.fillStyle = '#fff';
  ctx.fillText(it.icon, S.W / 2, py + 96);
  ctx.font = 'bold 22px system-ui'; ctx.fillStyle = hexA(col, 1);
  ctx.fillText(it.name, S.W / 2, py + 130);
  ctx.font = 'bold 10px system-ui'; ctx.fillStyle = hexA(col, 0.85);
  ctx.fillText(it.tier.toUpperCase(), S.W / 2, py + 148);
  ctx.font = '14px system-ui'; ctx.fillStyle = '#d8c2c8';
  ctx.fillText(it.desc, S.W / 2, py + 174);
  ctx.font = 'bold 14px system-ui'; ctx.fillStyle = affordable ? '#ff9d9d' : '#ff5d5d';
  ctx.fillText(affordable ? ('Costs ' + cost + ' HP now  ·  −' + cost + ' max HP permanently')
                          : 'Not enough HP to make this sacrifice', S.W / 2, py + 202);

  const bw = (pw - 56 - 16) / 2, bh = 50, by = py + ph - bh - 22;
  const yx = px + 28, nx = yx + bw + 16;
  ctx.textBaseline = 'middle';
  const yhot = affordable && pointIn(aim, yx, by, bw, bh);   // SACRIFICE (yes)
  ctx.fillStyle = !affordable ? 'rgba(120,40,48,.4)' : (yhot ? '#e1485a' : '#b73a48');
  roundRect(yx, by, bw, bh, 12); ctx.fill();
  ctx.fillStyle = affordable ? '#fff' : 'rgba(255,255,255,.45)'; ctx.font = 'bold 16px system-ui';
  ctx.fillText('🩸 SACRIFICE', yx + bw / 2, by + bh / 2);
  if (affordable) S.uiButtons.push({ x: yx, y: by, w: bw, h: bh, action: confirmBlood });
  const nhot = pointIn(aim, nx, by, bw, bh);                 // CANCEL (no)
  ctx.fillStyle = nhot ? 'rgba(255,255,255,.16)' : 'rgba(255,255,255,.08)';
  roundRect(nx, by, bw, bh, 12); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.18)'; ctx.lineWidth = 1.4; ctx.stroke();
  ctx.fillStyle = '#eaf0ff'; ctx.font = 'bold 16px system-ui';
  ctx.fillText('CANCEL', nx + bw / 2, by + bh / 2);
  S.uiButtons.push({ x: nx, y: by, w: bw, h: bh, action: () => { S.bloodConfirm = null; } });

  ctx.fillStyle = '#8a7a80'; ctx.font = '11px system-ui'; ctx.textBaseline = 'alphabetic';
  ctx.fillText('Y / Enter  ·  N / Esc', S.W / 2, by + bh + 16);
  ctx.textAlign = 'left';
}
// ---- Gambler's Shrine: trade a random item you own for a freshly-rolled one, for an escalating cut of your wealth ----
export function gambleCost() {
  const pct = Math.min(0.6, 0.2 + 0.1 * S.gambleCount);   // 20% of your peak coins, +10pts per reroll, capped at 60%
  return Math.max(5, Math.round(S.maxCoinsRun * pct));
}
export function doGambleReroll(s) {
  const cost = gambleCost();
  if (S.coinsRun < cost) { popText({ x: s.x, y: s.y - 12 }, 'need 🪙 ' + cost); Audio.play('click'); return; }
  S.coinsRun -= cost; S.gambleCount++;
  // pull one random copy out of your inventory (weighted by how many you hold)…
  const owned = [];
  for (const k in S.items) for (let i = 0; i < S.items[k]; i++) owned.push(k);
  let lost = null;
  if (owned.length) {
    const k = owned[Math.floor(rnd() * owned.length)];
    if (--S.items[k] <= 0) delete S.items[k];
    lost = (ITEMS.find(it => it.key === k) || {}).name || k;
  }
  // …and roll a fresh one in at honest tier odds (usually common, sometimes a jackpot)
  const got = randomItemOfTier(rollTier());
  grantItem(got.key);
  // dice crumble after 3 pulls (0.16): shrines persist on the map now, and gamble was the only kind
  // that never retired — immortal dice would slowly ratchet the shrine cap full of themselves
  s.uses = (s.uses || 0) + 1;
  if (s.uses >= BAL.shrines.gambleUses) { s.taken = true; popText({ x: s.x, y: s.y - 30 }, 'the dice crumble'); }
  const rare = got.tier === 'legendary' || got.tier === 'lunar' || got.tier === 'mythic';
  Audio.play('shrine'); Audio.play(rare ? 'powerup' : 'coin');
  popText({ x: s.x, y: s.y - 12 }, (lost ? lost + ' → ' : '') + got.name);
  for (let i = 0; i < (rare ? 28 : 14); i++) spawnParticle(s.x, s.y, rare ? 'gold' : 'bounce');
  if (rare) { S.shake = Math.max(S.shake, 8); S.flash = Math.max(S.flash, S.save.settings.reduceFlash ? 0.08 : 0.2); }
}

// ---- Pandora's Box (0.14 rework, by player request): CHOOSE which item stacks to transmute — every
// copy of a chosen stack rerolls into a random item of the SAME rarity. "Fix my build", not "torch it".
export function openPandora(s, keys) {
  for (const k of keys) {
    const def = ITEMS.find(i => i.key === k); if (!def || !S.items[k]) continue;
    const n = S.items[k];
    delete S.items[k];
    for (let i = 0; i < n; i++) { const it = randomItemOfTier(def.tier); S.items[it.key] = (S.items[it.key] || 0) + 1; }
  }
  const oldMax = S.MAX_HP; recomputeStats();
  if (S.MAX_HP > oldMax) player.hp += S.MAX_HP - oldMax;     // credit any max-HP gain; clamp the rest
  player.hp = Math.max(1, Math.min(S.MAX_HP, player.hp));
  s.taken = true;
  Audio.play('cascade'); S.shake = Math.max(S.shake, 12); S.flash = Math.max(S.flash, S.save.settings.reduceFlash ? 0.12 : 0.32);
  for (let i = 0; i < 44; i++) spawnParticle(s.x, s.y, 'gold');
  popText({ x: player.x, y: player.y - 26 }, '🎁 PANDORA!');
}
export function confirmPandora() {
  const pc = S.pandoraConfirm; if (!pc) return;
  const keys = Object.keys(pc.sel).filter(k => pc.sel[k] && S.items[k]);
  if (!keys.length) { Audio.play('click'); return; }     // nothing chosen — pick stacks or cancel
  openPandora(pc.s, keys);
  S.pandoraConfirm = null;
}
export function pandoraConfirmOverlay() {
  const pc = S.pandoraConfirm; if (!pc) return;
  S.uiButtons = [];
  const owned = ITEMS.filter(i => S.items[i.key]);
  const selKeys = owned.filter(i => pc.sel[i.key]);
  const selCopies = selKeys.reduce((s2, i) => s2 + S.items[i.key], 0);
  ctx.fillStyle = 'rgba(6,10,24,.86)'; ctx.fillRect(0, 0, S.W, S.H);
  const pw = Math.min(620, S.W - 32), ph = Math.min(500, S.H - 32), px = (S.W - pw) / 2, py = (S.H - ph) / 2;
  ctx.fillStyle = 'rgba(22,18,8,.97)'; roundRect(px, py, pw, ph, 18); ctx.fill();
  ctx.strokeStyle = 'rgba(255,210,74,.5)'; ctx.lineWidth = 1.5; ctx.stroke();
  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#ffe08a'; ctx.font = 'bold 18px system-ui';
  ctx.fillText('🎁 PANDORA’S BOX', S.W / 2, py + 38);
  ctx.font = '13px system-ui'; ctx.fillStyle = '#e7dcc0';
  ctx.fillText('Pick the stacks to transmute — every copy rerolls into a random item of the SAME rarity.', S.W / 2, py + 60);

  // selectable chips: one per owned stack, ×count, gold border when chosen
  const chipW = 120, chipH = 44, cgap = 8;
  const cols = Math.max(1, Math.floor((pw - 56 + cgap) / (chipW + cgap)));
  const gridY = py + 78, gridMaxY = py + ph - 96;
  const rowsFit = Math.max(1, Math.floor((gridMaxY - gridY + cgap) / (chipH + cgap)));
  const maxChips = cols * rowsFit;
  const show = owned.slice(0, maxChips);
  if (!owned.length) {
    ctx.fillStyle = '#8a8470'; ctx.font = '14px system-ui';
    ctx.fillText('You have no items to transmute yet.', S.W / 2, gridY + 40);
  }
  show.forEach((it, i) => {
    const cx = px + 28 + (i % cols) * (chipW + cgap);
    const cy = gridY + Math.floor(i / cols) * (chipH + cgap);
    const on = !!pc.sel[it.key], col = TIERS[it.tier].color;
    ctx.fillStyle = on ? hexA('#ffd24a', 0.16) : 'rgba(255,255,255,.05)';
    roundRect(cx, cy, chipW, chipH, 9); ctx.fill();
    ctx.strokeStyle = on ? '#ffd24a' : hexA(col, 0.5); ctx.lineWidth = on ? 2 : 1.3; ctx.stroke();
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.font = '18px system-ui'; ctx.fillStyle = '#fff'; ctx.fillText(it.icon, cx + 8, cy + chipH / 2 + 1);
    ctx.font = 'bold 11px system-ui'; ctx.fillStyle = hexA(col, 1);
    ctx.fillText(fitTip(it.name, chipW - 64, cx + 32, cy + 8, 14), cx + 32, cy + 15);
    ctx.font = 'bold 11px system-ui'; ctx.fillStyle = on ? '#ffd24a' : '#8a97c4';
    ctx.fillText('×' + S.items[it.key] + (on ? '  ✓' : ''), cx + 32, cy + 31);
    S.uiButtons.push({ x: cx, y: cy, w: chipW, h: chipH, action: () => { pc.sel[it.key] = !pc.sel[it.key]; Audio.play('click'); } });
  });
  if (owned.length > show.length) {
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillStyle = '#8a8470'; ctx.font = 'bold 11px system-ui';
    ctx.fillText('+' + (owned.length - show.length) + ' more not shown', S.W / 2, gridMaxY + 12);
  }

  const bw = (pw - 56 - 16) / 2, bh = 50, by = py + ph - bh - 22;
  const yx = px + 28, nx = yx + bw + 16;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  const can = selKeys.length > 0;
  const yhot = can && pointIn(aim, yx, by, bw, bh);
  ctx.fillStyle = !can ? 'rgba(120,100,40,.35)' : (yhot ? '#e0b53a' : '#c79a2a'); roundRect(yx, by, bw, bh, 12); ctx.fill();
  ctx.fillStyle = can ? '#1a1402' : 'rgba(255,255,255,.4)'; ctx.font = 'bold 15px system-ui';
  ctx.fillText(can ? '🎁 TRANSMUTE ' + selCopies : 'pick stacks above', yx + bw / 2, by + bh / 2);
  if (can) S.uiButtons.push({ x: yx, y: by, w: bw, h: bh, action: confirmPandora });
  const nhot = pointIn(aim, nx, by, bw, bh);
  ctx.fillStyle = nhot ? 'rgba(255,255,255,.16)' : 'rgba(255,255,255,.08)'; roundRect(nx, by, bw, bh, 12); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.18)'; ctx.lineWidth = 1.4; ctx.stroke();
  ctx.fillStyle = '#eaf0ff'; ctx.font = 'bold 16px system-ui'; ctx.fillText('CANCEL', nx + bw / 2, by + bh / 2);
  S.uiButtons.push({ x: nx, y: by, w: bw, h: bh, action: () => { S.pandoraConfirm = null; } });
  ctx.fillStyle = '#8a8470'; ctx.font = '11px system-ui'; ctx.textBaseline = 'alphabetic';
  ctx.fillText('click stacks to select  ·  Y / Enter transmute  ·  N / Esc cancel', S.W / 2, by + bh + 16);
  ctx.textAlign = 'left';
}

// ---- Boss reward: a pick-1-of-3 draft on every boss kill (replaces the old random auto-grant) ----
// roll an ITEM, biased toward the build you're already committed to (so bosses deepen a build, not dilute it)
export function rollRewardItem() {
  const _tr = rnd();
  const tier = _tr < 0.3 ? 'legendary' : _tr < 0.7 ? 'epic' : 'uncommon';
  // prefer your dominant archetype — and with NO committed build yet, pull toward your leading
  // tag 60% of the time, so dabblers (one venomtip…) get drawn INTO a build instead of staying
  // generalist forever (39% of ≤0.6 runs ended generalist; DoT archetypes never got off the ground)
  let tag = S.buildTag;
  if (!tag) {
    let bv = 0;
    for (const t in S.arch) if (S.arch[t] > bv) { bv = S.arch[t]; tag = t; }
    if (!bv || rnd() < 0.4) tag = null;
  }
  if (tag) {
    const onTheme = ITEMS.filter(i => i.tier === tier && ITEM_TAGS[i.key] === tag && offerWeightMul(i.key) > 0);   // boss-draft theme slot respects the hide rules too (0.19)
    if (onTheme.length) return onTheme[Math.floor(rnd() * onTheme.length)];
  }
  return randomItemOfTier(tier);
}
export function bossItemChoice(item) {
  const note = weaponInteractionNote(item.key) || buildPrereqNote(item.key);
  return { kind: 'item', item, icon: item.icon, name: item.name, tier: item.tier,
    lines: note ? [item.desc, note] : [item.desc],
    diff: statDiffLines(item.key),
    sub: item.tier === 'mythic' ? '🜲 MYTHIC — bends the rules'
       : (ITEM_TAGS[item.key] && ARCH_INFO[ITEM_TAGS[item.key]] ? ARCH_INFO[ITEM_TAGS[item.key]].icon + ' ' + ARCH_INFO[ITEM_TAGS[item.key]].name + ' item' : 'run item') };
}
export function rollBossChoices() {
  const gear = makeGear(rnd() < 0.65 ? 'legendary' : 'uncommon');
  // 3% of boss drafts surface a MYTHIC in the item slot — one of the three ways they exist at all
  const item = (rnd() < BAL.mythic.bossOdds ? randomMythic() : null) || rollRewardItem();
  const cash = 60 + S.waveNum * 8;   // the safe pick: a big purse + a full patch-up
  const out = [
    { kind: 'gear', gear, icon: gear.icon, name: gear.name, tier: gear.tier,
      lines: gear.lines.slice(), sub: gear.uniqueDesc ? '★ ' + gear.uniqueDesc : 'equipment' },
    bossItemChoice(item),
    { kind: 'cash', icon: '🪙', name: 'Windfall', tier: 'uncommon',
      lines: ['+' + cash + ' coins', 'full heal'], sub: 'play it safe', coins: cash },
  ];
  if (S.items.architect) out.push(bossItemChoice(rollRewardItem()));   // Architect's Pen: a 4th choice on every draft
  return out;
}
export function openBossReward() {
  S.bossReward = { rerolls: 0, choices: rollBossChoices() };
  Audio.play('boss');
}
// paid reroll of all three choices — a coin sink with teeth (0.10): cost scales with wave AND
// escalates per pull, so deep-run wealth buys re-draws but can't fish a perfect legendary for free
export function bossRerollCost() {
  if (S.items.architect && S.bossReward && S.bossReward.rerolls === 0) return 0;   // Architect's Pen: the first pull is free
  return Math.round(Math.max(40 * Math.max(1, S.waveNum), S.coinsRun * 0.12) * (1 + (S.bossReward ? S.bossReward.rerolls : 0)));   // floored at 12% of held coins (0.16) — wealth can re-draw, not fish
}
export function rerollBossReward() {
  if (!S.bossReward) return;
  const cost = bossRerollCost();
  if (S.coinsRun < cost) { Audio.play('click'); return; }
  S.coinsRun -= cost; S.bossReward.rerolls++; S.bossRerollsRun++;
  S.bossReward.choices = rollBossChoices();
  Audio.play('shrine');
}
export function takeBossReward(i) {
  if (!S.bossReward) return;
  const c = S.bossReward.choices[i]; if (!c) return;
  if (c.kind === 'gear') grantGear(c.gear);
  else if (c.kind === 'item') grantItem(c.item.key); else {   // cash: a purse + a clean patch-up
    S.coinsRun += c.coins; S.coinsEarned += c.coins;
    player.hp = S.MAX_HP;
  }
  S.bossReward = null;
  tryOpenLevelUp();   // a boss kill often crosses a level — show those drafts now that the reward is claimed
  player.iFrames = Math.max(player.iFrames, 60);   // brief grace as the action resumes
  const rare = c.tier === 'legendary' || c.tier === 'lunar' || c.tier === 'mythic';
  Audio.play(rare ? 'powerup' : 'shrine');
  for (let k = 0; k < (rare ? 30 : 16); k++) spawnParticle(player.x, player.y, rare ? 'gold' : 'bounce');
  if (rare) { S.shake = Math.max(S.shake, 8); S.flash = Math.max(S.flash, S.save.settings.reduceFlash ? 0.08 : 0.2); }
}
export function drawCardChrome(x, y, w, h, tcol, special) {   // draft-card backing + rarity border (legendary/mythic glow)
  ctx.fillStyle = 'rgba(255,255,255,.04)'; roundRect(x, y, w, h, 12); ctx.fill();
  if (special) {
    ctx.save(); ctx.shadowColor = tcol; ctx.shadowBlur = 12 + 7 * Math.sin(performance.now() * 0.005);
    ctx.strokeStyle = tcol; ctx.lineWidth = 2.5; roundRect(x, y, w, h, 12); ctx.stroke(); ctx.restore();
  } else { ctx.strokeStyle = hexA(tcol, 0.6); ctx.lineWidth = 2; roundRect(x, y, w, h, 12); ctx.stroke(); }
}
export function bossRewardOverlay() {
  if (!S.bossReward) return;
  S.uiButtons = [];
  ctx.fillStyle = 'rgba(6,10,24,.88)'; ctx.fillRect(0, 0, S.W, S.H);
  const nch = S.bossReward.choices.length;   // 3, or 4 with Architect's Pen
  const L = cardLayout(nch, { maxW: 760, footer: 56 });
  const { px, py, pw, ph } = L, cardW = L.cardW, cardH = L.cardH;
  ctx.fillStyle = 'rgba(14,12,26,.97)'; roundRect(px, py, pw, ph, 20); ctx.fill();
  ctx.strokeStyle = 'rgba(255,141,110,.5)'; ctx.lineWidth = 1.5; ctx.stroke();

  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#ff8d6e'; ctx.font = 'bold 26px system-ui';
  ctx.fillText('☠ BOSS DOWN', S.W / 2, py + 46);
  ctx.fillStyle = '#9fb0e0'; ctx.font = '14px system-ui';
  ctx.fillText('Claim ONE reward — choose your spoils', S.W / 2, py + 70);

  for (let i = 0; i < nch; i++) {
    const c = S.bossReward.choices[i], x = L.cardX(i), cy = L.cardY(i);
    const tcol = TIERS[c.tier] ? TIERS[c.tier].color : '#cdd6ff';
    const special = c.tier === 'legendary' || c.tier === 'lunar' || c.tier === 'mythic';
    drawCardChrome(x, cy, cardW, cardH, tcol, special);
    autoRing(i, x, cy, cardW, cardH);

    // flow top→bottom, clamped above the TAKE button; a short (responsive) card drops the stat preview / sub
    const compact = cardH < 236;
    const btnTop = cy + cardH - 52;
    ctx.textAlign = 'center';
    ctx.font = (compact ? 30 : 38) + 'px system-ui'; ctx.fillStyle = '#fff'; ctx.fillText(c.icon, x + cardW / 2, cy + (compact ? 40 : 56));
    ctx.font = 'bold ' + (compact ? 15 : 17) + 'px system-ui'; ctx.fillStyle = tcol; ctx.fillText(fitText(c.name, cardW - 20), x + cardW / 2, cy + (compact ? 64 : 84));
    ctx.font = '10px system-ui'; ctx.fillStyle = hexA(tcol, 0.9); ctx.fillText(c.tier.toUpperCase(), x + cardW / 2, cy + (compact ? 78 : 100));
    ctx.textAlign = 'left'; ctx.font = '12px system-ui'; ctx.fillStyle = '#cdd6ff';
    let ly = cy + (compact ? 96 : 120);
    for (const line of c.lines) { if (ly > btnTop - 16) break; ly = wrapText('• ' + line, x + 16, ly + 4, cardW - 32, 15, compact ? 2 : 3) + 16; }
    if (c.diff && c.diff.length && !compact) {   // stat preview: current → with this item
      ctx.font = '11px system-ui'; ctx.fillStyle = '#9be8c0';
      let k = 0;
      for (; k < Math.min(3, c.diff.length) && ly < btnTop - 28; k++, ly += 13) ctx.fillText(fitText(c.diff[k], cardW - 32), x + 16, ly);
      if (c.diff.length > k && ly < btnTop - 28) { ctx.fillStyle = '#6f7ba6'; ctx.fillText('+' + (c.diff.length - k) + ' more', x + 16, ly); ly += 13; }
    }
    if (c.sub && ly < btnTop - 14) { ctx.fillStyle = '#ffd66e'; ctx.font = 'italic 11px system-ui'; wrapText(c.sub, x + 16, ly + 2, cardW - 32, 14, 2); }

    const bw = cardW - 28, bh = 40, bx = x + 14, by = cy + cardH - bh - 12;
    const hot = pointIn(aim, bx, by, bw, bh);
    ctx.fillStyle = hot ? '#36c07a' : '#2a9c63'; roundRect(bx, by, bw, bh, 9); ctx.fill();
    ctx.fillStyle = '#fff'; ctx.font = 'bold 15px system-ui'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('TAKE  [' + (i + 1) + ']', bx + bw / 2, by + bh / 2);
    ctx.textBaseline = 'alphabetic';
    S.uiButtons.push({ x: bx, y: by, w: bw, h: bh, action: () => takeBossReward(i) });
  }
  // paid reroll of the whole draft — visible coin sink; cost escalates per pull
  const rc = bossRerollCost(), canRe = S.coinsRun >= rc;
  const rw2 = 260, rh2 = 34, rx2 = (S.W - rw2) / 2, ry2 = py + ph - rh2 - 10;
  const rhot2 = canRe && pointIn(aim, rx2, ry2, rw2, rh2);
  ctx.fillStyle = canRe ? (rhot2 ? '#3aa0ff' : '#2b6fd6') : 'rgba(120,130,160,.25)';
  roundRect(rx2, ry2, rw2, rh2, 9); ctx.fill();
  ctx.fillStyle = canRe ? '#fff' : '#7a86ad'; ctx.font = 'bold 13px system-ui';
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('↻ REROLL ALL  🪙 ' + rc + '   [R]', rx2 + rw2 / 2, ry2 + rh2 / 2);
  ctx.textBaseline = 'alphabetic';
  if (canRe) S.uiButtons.push({ x: rx2, y: ry2, w: rw2, h: rh2, action: rerollBossReward });
  ctx.textAlign = 'left';
}

// ---- Item Cache overlay: three priced items, buy ONE or walk away (the cache stays in the world) ----
export function shrineDraftOverlay() {
  if (!S.shrineDraft) return;
  const s = S.shrineDraft.s;
  S.uiButtons = [];
  ctx.fillStyle = 'rgba(6,10,24,.88)'; ctx.fillRect(0, 0, S.W, S.H);
  const nch = s.choices.length;   // 3, or 4 with Architect's Pen
  const showBuild = S.H >= 520;   // the "your build" strip needs vertical room — dropped on short screens (press J)
  const L = cardLayout(nch, { maxW: 760, footer: showBuild ? 96 : 52 });
  const { px, py, pw, ph } = L, cardW = L.cardW, cardH = L.cardH;
  ctx.fillStyle = 'rgba(10,20,24,.97)'; roundRect(px, py, pw, ph, 20); ctx.fill();
  ctx.strokeStyle = 'rgba(127,224,192,.5)'; ctx.lineWidth = 1.5; ctx.stroke();

  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#7fe0c0'; ctx.font = 'bold 26px system-ui';
  ctx.fillText('🛒 ITEM CACHE', S.W / 2, py + 46);
  ctx.fillStyle = '#9fb0e0'; ctx.font = '14px system-ui';
  ctx.fillText('Buy ONE — or walk away (it keeps)  ·  you have 🪙 ' + S.coinsRun, S.W / 2, py + 70);

  for (let i = 0; i < nch; i++) {
    const it = s.choices[i]; if (!it) continue;
    const cost = shrineItemPrice(it), can = S.coinsRun >= cost;
    const tcol = TIERS[it.tier].color, x = L.cardX(i), cy = L.cardY(i);
    const special = it.tier === 'legendary' || it.tier === 'lunar' || it.tier === 'mythic';
    drawCardChrome(x, cy, cardW, cardH, tcol, special);
    autoRing(i, x, cy, cardW, cardH);

    // everything FLOWS top→bottom and stops before the buy button, so a short (responsive) card drops the
    // stat preview / tag / owned line rather than overlapping the description (compact = a short card)
    const compact = cardH < 236;
    const bw = cardW - 28, bh = 40, bx = x + 14, by = cy + cardH - bh - 12;
    ctx.textAlign = 'center';
    ctx.font = (compact ? 30 : 38) + 'px system-ui'; ctx.fillStyle = '#fff'; ctx.fillText(it.icon, x + cardW / 2, cy + (compact ? 40 : 56));
    ctx.font = 'bold ' + (compact ? 15 : 17) + 'px system-ui'; ctx.fillStyle = tcol; ctx.fillText(fitText(it.name, cardW - 20), x + cardW / 2, cy + (compact ? 64 : 84));
    ctx.font = '10px system-ui'; ctx.fillStyle = hexA(tcol, 0.9); ctx.fillText(it.tier.toUpperCase(), x + cardW / 2, cy + (compact ? 78 : 100));
    let ty = cy + (compact ? 94 : 116);
    const wnote = weaponInteractionNote(it.key) || buildPrereqNote(it.key);   // weapon-mode quirks, or "inert without a source" warnings
    if (wnote && !compact) {
      ctx.font = 'italic 10px system-ui'; ctx.fillStyle = '#ffb74d';
      ctx.fillText(fitTip(wnote, cardW - 16, x + 8, ty - 8, 14), x + cardW / 2, ty); ty += 14;
    }
    ctx.font = '12px system-ui'; ctx.fillStyle = '#cdd6ff';
    ty = wrapText(it.desc, x + cardW / 2, ty + 8, cardW - 28, 15, compact ? 2 : 3) + 14;
    const dl = S.shrineDraft.diffs && S.shrineDraft.diffs[i];   // stat preview ("armor 8% → 16%") — only on roomy cards
    if (dl && dl.length && !compact) {
      ctx.font = '11px system-ui'; ctx.fillStyle = '#9be8c0';
      let k = 0;
      for (; k < dl.length && k < 3 && ty < by - 28; k++, ty += 13) ctx.fillText(fitText(dl[k], cardW - 24), x + cardW / 2, ty);
      if (dl.length > k && ty < by - 28) { ctx.fillStyle = '#6f7ba6'; ctx.fillText('+' + (dl.length - k) + ' more', x + cardW / 2, ty); ty += 13; }
    }
    const tag = ITEM_TAGS[it.key];
    if (ty < by - 14) {
      ctx.fillStyle = '#ffd66e'; ctx.font = 'italic 11px system-ui';
      ctx.fillText(tag && ARCH_INFO[tag] ? ARCH_INFO[tag].icon + ' ' + ARCH_INFO[tag].name + ' item' : 'run item', x + cardW / 2, ty); ty += 14;
    }
    const owned = S.items[it.key] || 0;
    if (owned > 0 && ty < by - 12) { ctx.fillStyle = '#6f7ba6'; ctx.font = 'italic 11px system-ui'; ctx.fillText('owned ×' + owned, x + cardW / 2, ty); }

    const hot = can && pointIn(aim, bx, by, bw, bh);
    ctx.fillStyle = can ? (hot ? '#36c07a' : '#2a9c63') : 'rgba(120,130,160,.25)';
    roundRect(bx, by, bw, bh, 9); ctx.fill();
    ctx.fillStyle = can ? '#fff' : '#7a86ad'; ctx.font = 'bold 14px system-ui'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('BUY 🪙 ' + cost + '  [' + (i + 1) + ']', bx + bw / 2, by + bh / 2);
    ctx.textBaseline = 'alphabetic';
    if (can) S.uiButtons.push({ x: bx, y: by, w: bw, h: bh, action: () => buyShrineDraft(i) });
  }

  // your current build (stacked ×counts) — the context the buy decision needs (room permitting)
  if (showBuild) {
    const ownedList = ITEMS.filter(i => S.items[i.key]);
    ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.font = 'bold 11px system-ui'; ctx.fillStyle = '#8a97c4';
    ctx.fillText(ownedList.length ? 'YOUR BUILD' : 'YOUR BUILD — empty so far', px + 28, py + ph - 84);
    let bx2 = px + 116; const maxBx = px + pw - 64; let hid = 0;
    for (const it of ownedList) {
      if (bx2 > maxBx) { hid++; continue; }
      ctx.font = '16px system-ui'; ctx.fillStyle = '#fff'; ctx.fillText(it.icon, bx2, py + ph - 84);
      ctx.font = 'bold 10px system-ui'; ctx.fillStyle = TIERS[it.tier].color; ctx.fillText('×' + S.items[it.key], bx2 + 18, py + ph - 80);
      bx2 += 42;
    }
    if (hid) { ctx.font = 'bold 11px system-ui'; ctx.fillStyle = '#8a97c4'; ctx.fillText('+' + hid + ' (J)', bx2, py + ph - 84); }
    ctx.textBaseline = 'alphabetic';
  }

  // walk away — no purchase, the cache stays where it was
  const lw = 220, lh = 34, lx = (S.W - lw) / 2, ly = py + ph - lh - 10;
  const lhot = pointIn(aim, lx, ly, lw, lh);
  ctx.fillStyle = lhot ? 'rgba(255,255,255,.16)' : 'rgba(255,255,255,.08)';
  roundRect(lx, ly, lw, lh, 9); ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,.18)'; ctx.lineWidth = 1.4; ctx.stroke();
  ctx.fillStyle = '#eaf0ff'; ctx.font = 'bold 13px system-ui'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('LEAVE   [E / Esc]', lx + lw / 2, ly + lh / 2);
  ctx.textBaseline = 'alphabetic';
  S.uiButtons.push({ x: lx, y: ly, w: lw, h: lh, action: closeShrineDraft });
  ctx.textAlign = 'left';
}

// ---- Per-run XP / leveling: kills earn XP; each level lets you draft a permanent (this-run) stat boost ----
// Level-up reward stats. amt = the COMMON-tier amount (scaled ×bigMult on uncommon/epic/lunar). pct stats
// read as a %, dec stats show one decimal, the rest round. neg = eligible to be the lunar curse. They
// accumulate as raw amounts on S.levelPerks (recomputeStats reads the totals — see stats.js).
export const LEVEL_STATS = [
  { key: 'dmg',       icon: '🔫', label: 'Damage',      amt: 0.09, pct: true, neg: true },
  { key: 'fire',      icon: '🔥', label: 'Fire Rate',   amt: 0.07, pct: true, neg: true },
  { key: 'hp',        icon: '❤️', label: 'Max HP',      amt: 25 },
  { key: 'regen',     icon: '💚', label: 'Regen',       amt: 2, unit: '/s' },
  { key: 'armor',     icon: '🛡', label: 'Armor',       amt: 0.05, pct: true, neg: true },
  { key: 'speed',     icon: '🌀', label: 'Move Speed',  amt: 3, dec: true, neg: true },
  { key: 'crit',      icon: '🎯', label: 'Crit',        amt: 0.05, pct: true, neg: true },
  { key: 'critdmg',   icon: '💥', label: 'Crit Dmg',    amt: 0.30, dec: true, neg: true },
  { key: 'range',     icon: '🔭', label: 'Range',       amt: 50 },
  { key: 'size',      icon: '⭕', label: 'Size',        amt: 0.10, pct: true, neg: true },
  { key: 'bspeed',    icon: '🚀', label: 'Proj Speed',  amt: 0.10, pct: true, neg: true },
  { key: 'elite',     icon: '👑', label: 'Elite Dmg',   amt: 0.20, pct: true, neg: true },
  { key: 'thorns',    icon: '🌵', label: 'Thorns',      amt: 1, int: true },
  { key: 'multishot', icon: '🔱', label: 'Projectiles', amt: 1, int: true },
  { key: 'pierce',    icon: '🏹', label: 'Pierce',      amt: 1, int: true },
  { key: 'bounce',    icon: '🔄', label: 'Bounces',     amt: 1, int: true },
  { key: 'exp',       icon: '✨', label: 'XP Gain',     amt: 0.12, pct: true, neg: true },
  { key: 'greed',     icon: '💰', label: 'Coins',       amt: 0.15, pct: true, neg: true },
  { key: 'luck',      icon: '🍀', label: 'Luck',        amt: 0.10, pct: true, neg: true },
];
// rarity → how many positive stats + whether they're the BIG magnitude; lunar also rolls one negative.
export const LEVEL_RARITIES = [
  { key: 'common',    color: '#b8c0d8', n: 1, big: false },
  { key: 'uncommon',  color: '#7be08a', n: 1, big: true },
  { key: 'rare',      color: '#6fb7ff', n: 2, big: false },
  { key: 'epic',      color: '#b884ff', n: 2, big: true },
  { key: 'legendary', color: '#ffb84d', n: 3, big: false },
  { key: 'lunar',     color: '#8af0e0', n: 3, big: true, neg: true },
];
const _SM = {}; for (const s of LEVEL_STATS) _SM[s.key] = s;
export function levelModText(m) {   // a single rolled stat → "+9% Damage" / "+25 Max HP" / "−7% Fire Rate"
  const s = _SM[m.key], v = Math.abs(m.amt), sign = m.amt < 0 ? '−' : '+';
  const num = s.pct ? Math.round(v * 100) + '%' : s.dec ? '' + (+v.toFixed(1)) : '' + Math.round(v);
  return sign + num + (s.unit || '') + ' ' + s.label;
}
// XP from level L to L+1 grows QUADRATICALLY (25·L·(L+1)): 1→2 = 50, 2→3 = 150, 3→4 = 300, 4→5 = 500 …
// super-linear on purpose — enemy spawn rate ramps with wave/Threat, so a linear cost would let leveling
// keep pace (or accelerate in HORDE/FRENZY). Quadratic cost outpaces the rising kill rate, so levels slow down.
export function xpCost(L) { return 25 * L * (L + 1); }
// integer stats (multishot/pierce/bounce/thorns) stay flat — scaled & rounded (≥1) instead of fractional.
const rollAmt = (s, mult) => s.int ? Math.max(1, Math.round(s.amt * mult)) : +(s.amt * mult).toFixed(4);
function rollLevelChoice() {   // one card: roll a rarity, then that many DISTINCT stats (+ a lunar curse)
  const cfg = BAL.lvlup, luck = S.luck || 0;   // Luck shifts the roll toward richer tiers
  let tot = 0; const w = LEVEL_RARITIES.map((r, i) => { const v = Math.max(0, (cfg[r.key] || 0) * (1 + luck * i)); tot += v; return v; });
  let x = rnd() * tot, rar = LEVEL_RARITIES[0];
  for (let i = 0; i < LEVEL_RARITIES.length; i++) { if (x < w[i]) { rar = LEVEL_RARITIES[i]; break; } x -= w[i]; }
  const mult = rar.big ? cfg.bigMult : 1;
  const pool = LEVEL_STATS.filter(s => !levelStatDead(s.key)), mods = [];   // never offer a stat that's inert for the build (e.g. Crit Dmg at 0% crit)
  const nStats = rar.n + (S.cardStatBonus || 0);   // Polymath (Scholar): every card rolls extra stats
  for (let k = 0; k < nStats && pool.length; k++) {
    const s = pool.splice(Math.floor(rnd() * pool.length), 1)[0];
    mods.push({ key: s.key, amt: rollAmt(s, mult) });
  }
  if (rar.neg) {   // lunar: one distinct negative stat (a curse; int stats are never cursed)
    const negPool = pool.filter(s => s.neg);
    if (negPool.length) { const s = negPool[Math.floor(rnd() * negPool.length)]; mods.push({ key: s.key, amt: -(+(s.amt * cfg.negMult).toFixed(4)) }); }
  }
  return { rarity: rar.key, color: rar.color, mods };
}
export function rollStatCards(n) { return Array.from({ length: n }, () => rollLevelChoice()); }   // shared by the level-up draft + the wave shop
export function rollLevelPerks() { return rollStatCards(Math.min(5, 3 + (S.items.architect ? 1 : 0) + (S.levelCards || 0))); }   // 3 cards · +1 Architect's Pen · +Scholar (deep Codex / Ascension); capped at 5 so the draft still fits
export function gainXP(n) {
  if (S.dead) return;
  S.xp += n * (S.xpMul || 1);   // level-up XP Gain stat
  while (S.xp >= S.xpNext) {   // each level: a stat card AND a talent point (Mentor talent: a bonus point every 2nd level)
    S.xp -= S.xpNext; S.level++; S.pendingLevels++; S.talentPts++;
    if (S.talentFlags.mentor && S.level % 2 === 0) S.talentPts++;
    if (S.talentFlags.prodigy) S.talentPts++;   // Prodigy (Scholar T5 fork): +1 bonus talent point every level
    // INSIGHT (0.22 scholar NEW PATH): weaponize the level-up EVENT itself — fired in updateCombat
    if (S.items.cram) S.cramT = 240;                                                 // Cram: a fire-rate/damage rush
    if (S.items.epiphany) S.insightQ = (S.insightQ || 0) + 1;                         // Epiphany: queue a level-scaled nova
    if (S.items.ascendant && S.level % 6 === 0) S.ascendQ = (S.ascendQ || 0) + 1;     // Ascendant Mind: every 6th level, a %-max-HP discharge
    S.xpNext = xpCost(S.level);
  }
  tryOpenLevelUp();
}
export function tryOpenLevelUp() {   // open the next queued level-up, but only once any other prompt/shop has resolved
  if (S.levelUp || S.bossReward || S.shopOpen || S.bloodConfirm || S.pandoraConfirm || S.shrineDraft || S.pendingLevels <= 0) return;
  S.pendingLevels--;
  S.levelUp = { choices: rollLevelPerks() };
  Audio.play('powerup');
  S.flash = Math.max(S.flash, S.save.settings.reduceFlash ? 0.06 : 0.16);
}
export function applyStatCard(c) {   // fold a rolled stat card's mods into S.levelPerks; a Max-HP gain heals
  const oldMax = S.MAX_HP;
  for (const m of c.mods) S.levelPerks[m.key] = (S.levelPerks[m.key] || 0) + m.amt;   // accumulate the rolled amounts
  recomputeStats();
  player.hp += Math.max(0, S.MAX_HP - oldMax);
  Audio.play('shrine');
  for (let k = 0; k < 14; k++) spawnParticle(player.x, player.y, 'gold');
}
// shared card renderer — the level-up draft AND the wave shop draw cards with this (same rarity/stat model)
export function drawStatCard(c, i, x, cy, cardW, cardH, onTake) {
  drawCardChrome(x, cy, cardW, cardH, c.color, c.rarity === 'legendary' || c.rarity === 'lunar');
  autoRing(i, x, cy, cardW, cardH);
  const compact = cardH < 220, btnTop = cy + cardH - 52;
  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.font = 'bold ' + (compact ? 16 : 19) + 'px system-ui'; ctx.fillStyle = hexA(c.color, 1);
  ctx.fillText(c.rarity.toUpperCase(), x + cardW / 2, cy + (compact ? 30 : 42));   // rarity heading
  // Build the rolled-stat lines (each stat + its optional weapon-mode note), then SHRINK-TO-FIT so a
  // rich card (legendary/lunar + Polymath can roll 5+ stats) never silently drops lines off the bottom.
  const baseF = compact ? 13 : 15, noteF = compact ? 10 : 11;
  const baseH = compact ? 18 : 22, noteH = compact ? 13 : 15;
  const lines = [];
  for (const m of c.mods) {   // negatives in red; delivery stats note the held weapon's behavior
    lines.push({ t: levelModText(m), col: m.amt < 0 ? '#ff7a8a' : '#bdf3d6', f: baseF, h: baseH, bold: true });
    const wn = weaponStatNote(m.key);   // e.g. "Bounces" on a Shock Maul → "↳ hurls shockwave lines"
    if (wn) lines.push({ t: '↳ ' + wn, col: '#ffb74d', f: noteF, h: noteH, bold: false });
  }
  const top = cy + (compact ? 40 : 56), avail = (btnTop - 6) - top;   // vertical room between heading and button
  let natural = lines.reduce((a, l) => a + l.h, 0);
  // When crowded, shed the secondary weapon-notes first (stats matter more) before scaling everything down.
  while (natural > avail && lines.some(l => !l.bold)) {
    const j = lines.map(l => l.bold).lastIndexOf(false);   // drop the last note
    natural -= lines[j].h; lines.splice(j, 1);
  }
  const scale = Math.min(1, avail / Math.max(1, natural));   // final uniform shrink for whatever remains
  let ty = top;
  for (const l of lines) {
    const h = l.h * scale;
    ctx.font = (l.bold ? 'bold ' : '') + Math.max(9, Math.round(l.f * scale)) + 'px system-ui';
    ctx.fillStyle = l.col;
    ctx.fillText(fitText(l.t, cardW - 16), x + cardW / 2, ty + h * 0.72);   // baseline near the slot bottom
    ty += h;
  }
  const bw = cardW - 28, bh = 40, bx = x + 14, by = cy + cardH - bh - 12;
  const hot = pointIn(aim, bx, by, bw, bh);
  ctx.fillStyle = hot ? '#36c07a' : '#2a9c63'; roundRect(bx, by, bw, bh, 9); ctx.fill();
  ctx.fillStyle = '#fff'; ctx.font = 'bold 15px system-ui'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('TAKE  [' + (i + 1) + ']', bx + bw / 2, by + bh / 2);
  ctx.textBaseline = 'alphabetic';
  S.uiButtons.push({ x: bx, y: by, w: bw, h: bh, action: onTake });
}
export function takeLevelUp(i) {
  if (!S.levelUp) return;
  const c = S.levelUp.choices[i]; if (!c) return;
  applyStatCard(c);
  S.levelUp = null;
  tryOpenLevelUp();   // chain through any further queued levels
}
export function levelUpOverlay() {
  if (!S.levelUp) return;
  S.uiButtons = [];
  ctx.fillStyle = 'rgba(6,10,24,.86)'; ctx.fillRect(0, 0, S.W, S.H);
  const nch = S.levelUp.choices.length;   // 3, or 4 with Architect's Pen
  const L = cardLayout(nch, { maxW: 760, footer: 24, idealCardH: 230 });
  const { px, py, pw, ph } = L, cardW = L.cardW, cardH = L.cardH;
  ctx.fillStyle = 'rgba(12,20,30,.97)'; roundRect(px, py, pw, ph, 20); ctx.fill();
  ctx.strokeStyle = 'rgba(125,255,176,.5)'; ctx.lineWidth = 1.5; ctx.stroke();

  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#7dffb0'; ctx.font = 'bold 26px system-ui';
  ctx.fillText('✦ LEVEL ' + S.level, S.W / 2, py + 46);
  ctx.fillStyle = '#9fb0e0'; ctx.font = '14px system-ui';
  ctx.fillText('Pick ONE upgrade   ·   ✦ +1 Talent Point earned (press N)' + (S.pendingLevels > 0 ? '   (+' + S.pendingLevels + ' more queued)' : ''), S.W / 2, py + 70);

  for (let i = 0; i < nch; i++) drawStatCard(S.levelUp.choices[i], i, L.cardX(i), L.cardY(i), cardW, cardH, () => takeLevelUp(i));
  ctx.textAlign = 'left';
}
// ---- Item Cache draft (0.11): an item shrine opens a freeze-and-buy-1-of-3 overlay, boss-draft style.
// Coins now buy a DECISION — you weigh three priced options (one leaning toward your build) or walk away.
// Leaving keeps the cache in the world, so you can come back richer.
export function openShrineDraft(s) {
  if (S.items.architect && s.choices.length === 3) s.choices.push(randomItemOfTier(rollTier()));   // Architect's Pen: a 4th slot, rolled on first open & kept on the pod
  S.shrineDraft = { s, diffs: s.choices.map(c => statDiffLines(c.key)) };   // "armor 8% → 16%" preview per card
  S.cacheOpens++; Audio.play('shrine');
}
export function buyShrineDraft(i) {
  if (!S.shrineDraft) return;
  const s = S.shrineDraft.s, it = s.choices[i]; if (!it) return;
  const cost = shrineItemPrice(it);
  if (S.coinsRun < cost) { Audio.play('click'); return; }
  S.coinsRun -= cost;
  grantItem(it.key);
  s.taken = true; S.shrineDraft = null; S.cacheBuys++;
  Audio.play('shrine');
  popText({ x: s.x, y: s.y }, it.desc);   // show WHAT it does, not just the name
  const rare = it.tier === 'legendary' || it.tier === 'lunar' || it.tier === 'mythic';
  for (let k = 0; k < (rare ? 30 : 16); k++) spawnParticle(s.x, s.y, 'bounce');
  if (rare) S.shake = Math.max(S.shake, 8);
  player.iFrames = Math.max(player.iFrames, 40);   // brief grace as the action resumes
  tryOpenLevelUp();
}
export function closeShrineDraft() {
  if (S.shrineDraft && S.shrineDraft.s) S.shrineDraft.s.snooze = true;   // don't instantly reopen under the player's feet
  S.shrineDraft = null; player.iFrames = Math.max(player.iFrames, 30); tryOpenLevelUp();
}
// E = interact: grab a dropped weapon, pay a Blood toll, open Pandora, or work a Gambler's Shrine.
// 0.14: targets the NEAREST interactable (was: any gun first, then shrine array order — overlapping
// spawns made it impossible to choose), and the reach is wider so sliding past doesn't drop the press.
export function tryInteract() {
  if (!S.started || S.dead || S.paused || S.shopOpen || S.bloodConfirm || S.pandoraConfirm || S.shrineDraft) return;
  let best = null, bd = Infinity, kind = null;
  for (const p of S.pickups) {
    if (p.got || p.kind !== 'weapon') continue;
    const d = Math.hypot(p.x - player.x, p.y - player.y);
    if (d < 96 && d < bd) { bd = d; best = p; kind = 'weapon'; }
  }
  for (const s of S.shrines) {
    if (s.taken) continue;
    const d = Math.hypot(s.x - player.x, s.y - player.y);
    if (d < player.r + 72 && d < bd) { bd = d; best = s; kind = s.kind; }
  }
  if (!best) return;
  if (kind === 'weapon') {
    best.got = true;
    if (WEAPON_IDX[best.wkey] != null && WEAPON_IDX[best.wkey] !== S.weapon) S.lastWeapon = S.weapon;   // Gemini remembers the gun you set down
    S.weapon = WEAPON_IDX[best.wkey] != null ? WEAPON_IDX[best.wkey] : S.weapon;
    recomputeStats();   // EVO is weapon-derived — swapping can evolve (or de-evolve) on the spot
    popText({ x: best.x, y: best.y - 10 }, WEAPONS[S.weapon].name + '!');
    for (let k = 0; k < 12; k++) spawnParticle(best.x, best.y, 'gold');
    S.shake = Math.max(S.shake, 6); Audio.play('powerup');
  }
  else if (kind === 'item')    { openShrineDraft(best); }                          // E also opens a cache (walk-in is the usual path)
  else if (kind === 'blood')   { S.bloodConfirm = best;   Audio.play('click'); }    // ask before paying the HP toll
  else if (kind === 'pandora') { S.pandoraConfirm = { s: best, sel: {} }; Audio.play('click'); }   // pick WHICH stacks to transmute
  else if (kind === 'reroll')  { doGambleReroll(best); }                           // snappy & repeatable: roll on the spot
  else if (kind === 'vault') {                                                     // one MYTHIC, steeply priced
    const vc = vaultPrice();
    if (S.coinsRun < vc) { popText({ x: best.x, y: best.y - 12 }, 'need 🪙 ' + vc); Audio.play('click'); return; }
    const m = randomMythic();
    if (!m) { popText({ x: best.x, y: best.y - 12 }, 'you hold every myth'); best.taken = true; return; }
    S.coinsRun -= vc;
    grantItem(m.key);
    best.taken = true;
    S.waveBanner = { text: '🜲 ' + m.name.toUpperCase(), life: 3 };
    popText({ x: player.x, y: player.y - 30 }, m.desc);
    Audio.play('cascade'); Audio.play('powerup');
    S.shake = Math.max(S.shake, 14); S.flash = Math.max(S.flash, S.save.settings.reduceFlash ? 0.12 : 0.35);
    for (let k = 0; k < 50; k++) spawnParticle(best.x, best.y, 'gold');
  }
}

// ---- Fervor / momentum (rewards relentless aggression; decays when you idle) ----
export function addFervor(amt) { S.fervor = Math.min(S.FERVOR_MAX, S.fervor + amt); }
export function updateFervor() {
  S.fervor = Math.max(0, S.fervor - S.FERVOR_DECAY);
  const T = BAL.fervor.tiers;
  const t = S.fervor >= T[3] ? 4 : S.fervor >= T[2] ? 3 : S.fervor >= T[1] ? 2 : S.fervor >= T[0] ? 1 : 0;
  if (t > S.fervorTier) { Audio.play('powerup'); if (t >= 4) S.flash = Math.max(S.flash, 0.25); }  // tier-up fanfare
  S.fervorTier = t;
  S.fvDmg   = 1 + t * BAL.fervor.dmg;     // each tier: more damage,
  S.fvFire  = 1 - t * BAL.fervor.fire;    //            faster fire,
  S.fvGreed = 1 + t * BAL.fervor.greed;   //            more coin value,
  S.fvSpeed = 1 + t * BAL.fervor.speed;   //            more move speed — tier 4 = OVERDRIVE
  // Gold Fever (greed keystone): the run's banked wealth becomes live firepower (caps so it can't fully replace a gun)
  S.goldMult = S.items.goldfever ? 1 + Math.min(1.8, S.coinsRun * 0.0004 * S.items.goldfever) : 1;   // 0.14 greed buff: 0.0003→0.0004/coin, cap +150%→+180% (sinks now compete for the same coins)
  // Dividend: every 60 coins EARNED (spending doesn't claw it back) → +1 flat damage per stack, cap +30/stack.
  // Greed's early-game combat conversion — its commons were pure economy, so greed runs spent waves 1-10 buying nothing that shoots.
  S.divBonus = S.items.dividend ? S.items.dividend * Math.min(30, Math.floor(S.coinsEarned / 60)) : 0;
  // Big Spender (greed variant keystone): the mirror of Gold Fever — SPENT coins (earned − held) become
  // firepower, so greed forks into hoarder (bank it) vs spender (burn it at every shop & cache)
  S.spendMult = S.items.bigspender ? 1 + Math.min(1.8, (S.coinsEarned - S.coinsRun) * 0.0004 * S.items.bigspender) : 1;
  // 0.18 coin-powered scalers, same frame-refreshed pattern: Compound Interest reads the BANK,
  // Golden Gun reads coins SPENT (Big Spender's flat-damage cousin)
  S.ciMult  = S.items.compoundinterest ? 1 + Math.min(0.2, Math.floor(S.coinsRun / 100) * 0.01) * S.items.compoundinterest : 1;
  S.ggBonus = S.items.goldengun ? Math.min(40 * S.items.goldengun, Math.floor(Math.max(0, S.coinsEarned - S.coinsRun) / 30)) : 0;
  // Heatsink: nearby burning foes stoke your gun — sampled every 15 frames (a full enemy scan per frame
  // for a passive aura read is wasted budget)
  if (S.items.heatsink) {
    if (S.frame % 15 === 0) {
      let nb = 0;
      for (const e of S.enemies) if (!e.dead && e.burnT > 0 && Math.hypot(e.x - player.x, e.y - player.y) < 300) nb++;
      S.pyroMult = 1 + Math.min(0.4, 0.04 * nb * S.items.heatsink);
    }
  } else S.pyroMult = 1;
  if (S.adrenalineT > 0) S.adrenalineT--;   // Adrenaline rush ticks down (refreshed by crits)
  if (S.chronoFreezeT > 0) S.chronoFreezeT--;   // stopped time resumes
  // Chronoshard (mythic): every 45 SECONDS, time fractures — a clock, not a kill counter, so slow
  // builds and boss stalemates get their stop too. Holds its charge until something is on the field.
  else if (S.items.chronoshard && ++S.chronoCharge >= 2700 && S.enemies.length) {
    S.chronoCharge = 0; S.chronoFreezeT = 150;   // global stop — enemies, their bullets & mortars halt; YOUR damage keeps flowing
    S.waveBanner = { text: '🧿 TIME FRACTURE', life: 1.6 };
    S.flash = Math.max(S.flash, S.save.settings.reduceFlash ? 0.08 : 0.2);
    Audio.play('cascade');
  }
}

// ---- Wave-clear: kill the wave early for a bonus + fast-forward ----
export function checkWaveClear() {
  if (S.waveCleared || S.waveNum <= 0 || S.waveNum % 10 === 0) return;   // boss waves are about the boss
  if (S.waveKills < S.waveQuota) return;
  for (const e of S.enemies) if (!e.dead) return;                    // still enemies on the field
  S.waveCleared = true;
  const bonus = Math.round((10 + S.waveNum * 2) * (1 + 0.15 * (S.items.coinpress || 0)));   // wave scaling 3→2 (0.16 income trim — the heal & free reroll stay); Coin Press fattens the payout
  dropCoins(player.x, player.y, bonus);   // capped entity count — a deep-wave clear pays 100s of coins' worth in a few fat coins
  player.hp = Math.min(S.MAX_HP, player.hp + S.MAX_HP * BAL.econ.waveClearHeal);   // small heal for clearing
  addFervor(20);
  S.nextShopBoon = true;                                             // next shop gets a free reroll
  if (S.waveTimer > 90) S.waveTimer = 90;                              // skip the dead air before the next wave
  S.waveBanner = { text: 'WAVE CLEAR  +' + bonus + ' 🪙', life: 1.8 };
  S.flash = Math.max(S.flash, 0.3); S.shake = Math.max(S.shake, 8);
  Audio.play('powerup');
}
